/**
 * TanStack Query hooks for webhook-endpoint admin CRUD (Phase 9.11).
 *
 * Wraps:
 *   GET    /api/v1/admin/webhooks            -> useWebhookEndpoints
 *   POST   /api/v1/admin/webhooks            -> useCreateWebhookEndpoint
 *   PATCH  /api/v1/admin/webhooks/{id}       -> useUpdateWebhookEndpoint
 *   DELETE /api/v1/admin/webhooks/{id}       -> useDeleteWebhookEndpoint
 *
 * The create response includes the HMAC signing `secret` exactly once.
 * The caller surfaces it via the show-once modal and does not persist
 * it anywhere.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  WebhookEndpointCreateRequest,
  WebhookEndpointCreateResponse,
  WebhookEndpointResponse,
  WebhookEndpointUpdateRequest,
} from "@/types/api";

export const WEBHOOK_ENDPOINTS_QUERY_KEY = ["admin", "webhooks"] as const;

export function useWebhookEndpoints(): UseQueryResult<
  WebhookEndpointResponse[]
> {
  return useQuery({
    queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<WebhookEndpointResponse[]>(
        "/admin/webhooks",
      );
      return response.data;
    },
  });
}

export function useCreateWebhookEndpoint(): UseMutationResult<
  WebhookEndpointCreateResponse,
  Error,
  WebhookEndpointCreateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: WebhookEndpointCreateRequest) => {
      const response = await api.post<WebhookEndpointCreateResponse>(
        "/admin/webhooks",
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY,
      });
    },
  });
}

export interface UpdateWebhookEndpointArgs {
  endpointId: string;
  body: WebhookEndpointUpdateRequest;
}

export function useUpdateWebhookEndpoint(): UseMutationResult<
  WebhookEndpointResponse,
  Error,
  UpdateWebhookEndpointArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ endpointId, body }) => {
      const response = await api.patch<WebhookEndpointResponse>(
        `/admin/webhooks/${endpointId}`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY,
      });
    },
  });
}

export function useDeleteWebhookEndpoint(): UseMutationResult<
  void,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (endpointId: string) => {
      await api.delete(`/admin/webhooks/${endpointId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY,
      });
    },
  });
}
